import React, { useEffect, useState } from 'react';
import { InputAutosuggest, Spinner } from '@vwfs-bronson/bronson-react';
import { Props } from '../../screens/FormPage/utils/fieldsPrivateCustomer';
import { autocomplete } from '../../services/api/address/autocomplete';
import { validate } from '../../services/api/address/validate';

interface SearchAddressBarParams {
  t: any;
  input: string;
  onChange(e): void;
  fieldFamilyIndex?: number;
  addressType?: string;
  formProps?: React.PropsWithChildren<Props>;
  isPrivate?: boolean;
  setIsCoCustomerSameAddress?: (value: boolean) => void;
}

type Address = {
  houseNumber?: string;
  street?: string;
  zipCode?: string;
  city?: string;
  countryCode?: string;
};

const centralPosition = '48.8566,2.3522';

const debounceTime = 300;

export function AddressSearchBar({
  t,
  input = '',
  onChange,
  formProps,
  setIsCoCustomerSameAddress,
}: SearchAddressBarParams) {
  const [lastInput, setLastInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<{ label: string; renderValue: string; countryCode?: string }[]>(
    []
  );

  useEffect(() => {
    if (input.trim().length < 3 || input === lastInput) return;

    if (!input) {
      setSearchResults([]);
    }

    const timeoutId = setTimeout(() => {
      setLastInput(input);
      const search = async () => {
        setIsLoading(true);
        try {
          const response = await autocomplete(input, centralPosition, 'FR');
          const mappedResults = response.map((item) => {
            return {
              label: item.label,
              renderValue: item.label,
              countryCode: item.countryCode,
            };
          });

          setSearchResults(mappedResults);
        } finally {
          setIsLoading(false);
        }
      };

      search();
    }, debounceTime);

    // eslint-disable-next-line consistent-return
    return () => clearTimeout(timeoutId);
  }, [input, lastInput]);

  const setCustomerFieldValues = (address: Address) => {
    formProps?.setFieldValue(
      'addressLine1',
      address.houseNumber ? `${address.houseNumber} ${address.street || ''}` : `${address.street || ''}`
    );
    formProps?.setFieldValue('zipCode', address?.zipCode || '');
    formProps?.setFieldValue('city', address?.city || '');
  };

  const selectAddress = async (selectedAddress: string) => {
    setSearchResults([]);
    const countryCode = searchResults.find((item) => item.label === selectedAddress)?.countryCode;

    if (!countryCode) {
      return;
    }

    try {
      setIsLoading(true);
      const { query, address } = await validate(selectedAddress, countryCode);
      if (formProps?.values !== undefined) {
        onChange(query);
        setLastInput(query);
        setCustomerFieldValues(address || {});
      }
      setIsCoCustomerSameAddress?.(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <InputAutosuggest
      placeholder={t('searchBar:addressSearch:placeholder')}
      value={input}
      list={searchResults?.map(({ renderValue }) => renderValue)}
      awesompleteProps={{
        filter: () => true,
      }}
      addonIcon={isLoading ? undefined : 'semantic-search'}
      addonSpinner={isLoading ? <Spinner /> : null}
      openOnFocus
      onChange={(e) => {
        onChange?.(e.target.value);
      }}
      onSelect={(e: any) => {
        selectAddress(e.text.value);
      }}
    />
  );
}
